import React, { useState, useRef, useEffect } from 'react';
import '../styles/ApplicationModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLinkedin, 
  faInstagram, 
  faFacebookSquare 
} from '@fortawesome/free-brands-svg-icons';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import DenyModal from './DenyModal';
import { 
  APPLICATION_STAGES, 
  getStageColor, 
  updateApplicationStage 
} from '../utils/applicationStages';
import { updateCalendar } from '../utils/calendarUtils';
import emailService from '../utils/email';

const ApplicationModal = ({ application, onClose, onUpdate }) => {
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showDenyModal, setShowDenyModal] = useState(false);
  const exportMenuRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [meetingDetails, setMeetingDetails] = useState({
    startTime: null,
    endTime: null,
    conferenceUrl: '',
    notes: ''
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExportClick = () => {
    setShowExportMenu(!showExportMenu);
  };

  const handleExportPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.width;
    
    // Title
    doc.setFontSize(20);
    doc.text('Application Details', pageWidth/2, 15, { align: 'center' });
    
    // Metadata
    doc.setFontSize(12);
    doc.text(`Submitted: ${formatDate(metadata.submittedAt)}`, 20, 25);
    doc.text(`Status: ${status}`, 20, 32);

    // Personal Information
    doc.setFontSize(16);
    doc.text('Personal Information', 20, 45);
    doc.setFontSize(12);
    doc.autoTable({
      startY: 50,
      head: [['Field', 'Value']],
      body: [
        ['Name', applicant.name || 'N/A'],
        ['Email', applicant.email || 'N/A'],
        ['Phone', applicant.phone || 'N/A']
      ],
      margin: { left: 20 }
    });

    // Cause Details
    doc.setFontSize(16);
    doc.text('Cause Details', 20, doc.lastAutoTable.finalY + 15);
    doc.setFontSize(12);
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Field', 'Value']],
      body: [
        ['Cause Name', project.name || 'N/A'],
        ['Cause URL', project.url || 'N/A'],
        ['Location', `${project.location?.city || ''}, ${project.location?.state || ''}`],
        ['Mission Statement', project.missionStatement || 'N/A'],
        ['Vision & Goals', project.visionGoals || 'N/A']
      ],
      margin: { left: 20 }
    });

    // Financial Information
    doc.setFontSize(16);
    doc.text('Financial Information', 20, doc.lastAutoTable.finalY + 15);
    doc.setFontSize(12);
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Category', 'Description', 'Amount']],
      body: [
        ...financial.budgetDetails?.map(item => [
          item.category,
          item.description,
          formatCurrency(item.amount)
        ]) || [],
        ['Total Budget', '', formatCurrency(
          (financial.budgetDetails || [])
            .reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
        )]
      ],
      margin: { left: 20 }
    });

    // Revenue Projections
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 10,
      head: [['Revenue Period', 'Amount']],
      body: [
        ['First Year Revenue', formatCurrency(financial.revenueProjections?.yearOne || 0)],
        ['Second Year Revenue', formatCurrency(financial.revenueProjections?.yearTwo || 0)]
      ],
      margin: { left: 20 }
    });

    // Social Media
    doc.setFontSize(16);
    doc.text('Social Media', 20, doc.lastAutoTable.finalY + 15);
    doc.setFontSize(12);
    doc.autoTable({
      startY: doc.lastAutoTable.finalY + 20,
      head: [['Platform', 'URL']],
      body: [
        ['LinkedIn', socialMedia.linkedin || 'N/A'],
        ['Instagram', socialMedia.instagram || 'N/A'],
        ['Facebook', socialMedia.facebook || 'N/A']
      ],
      margin: { left: 20 }
    });

    // Additional Comments
    if (additionalInfo.comments) {
      doc.setFontSize(16);
      doc.text('Additional Comments', 20, doc.lastAutoTable.finalY + 15);
      doc.setFontSize(12);
      doc.text(additionalInfo.comments, 20, doc.lastAutoTable.finalY + 25);
    }

    // Save the PDF
    doc.save(`application-${application._id}.pdf`);
    setShowExportMenu(false);
  };

  const handleEmailExport = () => {
    window.location.href = `mailto:${application.applicant.email}?subject=Application Documents - ${application.project.name}`;
    setShowExportMenu(false);
  };

  const handleLegalReview = async () => {
    await handleStageUpdate(APPLICATION_STAGES.LEGAL_REVIEW.key);
  };

  const handleRequestInfo = async () => {
    await handleStageUpdate(APPLICATION_STAGES.INFO_REQUESTED.key);
    // Then send email
    window.location.href = `mailto:${application.applicant.email}?subject=Additional Information Required - ${application.project.name}&body=Dear ${application.applicant.name},%0D%0A%0D%0AWe are reviewing your application for ${application.project.name} and require some additional information.%0D%0A%0D%0ABest regards,%0D%0ACause and Impact Team`;
  };

  const handleApprove = async () => {
    await handleStageUpdate(APPLICATION_STAGES.APPROVED.key);
  };

  const handleDenyClick = () => {
    setShowDenyModal(true);
  };

  const handleDenyConfirm = () => {
    // Refresh the application list or update the status
    onClose();
  };

  const handleStageUpdate = async (newStage, additionalData = {}) => {
    try {
      await updateApplicationStage(application._id, newStage, additionalData);
      
      // Handle specific stage actions
      switch (newStage) {
        case APPLICATION_STAGES.APPROVED.key:
          // Send approval email with Calendly link
          await emailService.sendApplicationApproval(application);
          break;
        case APPLICATION_STAGES.MEETING_SCHEDULED.key:
          // Update calendar and notifications
          await updateCalendar(additionalData.meetingDetails);
          break;
        // ... handle other stage-specific actions
      }
      
      // Refresh application data or close modal as needed
    } catch (error) {
      console.error('Error updating stage:', error);
      alert('Failed to update application stage. Please try again.');
    }
  };

  const handleApproval = async () => {
    setLoading(true);
    setError(null);
    
    try {
        await emailService.sendApplicationApproval(application); 
      
      // Only update calendar if meeting details exist
    if (meetingDetails.startTime) {
        await updateCalendar({
          ...meetingDetails,
          applicationId: application._id,
          projectName: application.project.name
        });
      }

      // Update application status
      await fetch(`/api/applications/${application._id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          status: 'APPROVED',
          metadata: { meetingDetails }
        }),
      });

      onUpdate && onUpdate();
      onClose && onClose();
    } catch (error) {
      console.error('Error in approval process:', error);
      setError('Failed to process approval. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Data validation
  if (!application) {
    console.warn('No application data provided to modal');
    return null;
  }

  // Access data using the correct path based on your MongoDB schema
  const {
    applicant = {},
    project = {},
    metadata = {},
    status,
    financial = {},
    organization = {},
    socialMedia = {},
    additionalInfo = {},
    fundraising = {},
    previousSponsorship = {},
    referral = {},
    legal = {}
  } = application;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={e => e.stopPropagation()}>
        <button className="back-button" onClick={onClose}>
          <i className="fas fa-arrow-left"></i> Back to Applications
        </button>
        
        <div className="modal-header">
          <h2>{application.project.name || 'Application Review'}</h2>
          <nav className="modal-actions">
            <div className="export-dropdown" ref={exportMenuRef}>
              <button className="export-btn" onClick={handleExportClick}>
                <i className="fas fa-download"></i> Export
              </button>
              {showExportMenu && (
                <div className="export-menu">
                  <button onClick={handleExportPDF}>
                    <i className="fas fa-file-pdf"></i> Download PDF
                  </button>
                  <button onClick={handleEmailExport}>
                    <i className="fas fa-envelope"></i> Email Documents
                  </button>
                  {application.documents?.map((doc, index) => (
                    <a 
                      key={index}
                      href={doc.url}
                      download
                      className="document-link"
                    >
                      <i className="fas fa-file"></i> {doc.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
            <button className="legal-btn" onClick={handleLegalReview}>
              <i className="fas fa-gavel"></i> Legal Review
            </button>
            <button className="request-info-btn" onClick={handleRequestInfo}>
              <i className="fas fa-question-circle"></i> Request Info
            </button>
            <button className="approve-btn" onClick={handleApprove}>
              <i className="fas fa-check"></i> Approve
            </button>
            <button className="deny-btn" onClick={handleDenyClick}>
              <i className="fas fa-times"></i> Deny
            </button>
          </nav>
        </div>

        <div className="modal-body">
          {/* Submission Metadata */}
          <div className="application-section">
            <h3>Submission Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Submission Date</label>
                <div>{formatDate(metadata.submittedAt)}</div>
              </div>
              <div className="info-item">
                <label>Device Info</label>
                <div>{metadata.userAgent || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>IP Address</label>
                <div>{metadata.ipAddress || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>Application Status</label>
                <div className={`status-badge ${status}`}>{status}</div>
              </div>
            </div>
          </div>

          {/* Personal Information */}
          <div className="application-section">
            <h3>Personal Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Name</label>
                <div>{applicant.name || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>Email</label>
                <div>{applicant.email || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>Phone</label>
                <div>{applicant.phone || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Legal Authority */}
          <div className="application-section">
            <h3>Legal Authority</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Signing Authority</label>
                <div>{legal.hasSigningAuthority ? 'Yes' : 'No'}</div>
              </div>
              <div className="info-item">
                <label>Authority Title/Role</label>
                <div>{legal.authorityRole || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>Deposit Agreement Accepted</label>
                <div>{legal.depositAgreement ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>

          {/* Cause Details - Enhanced */}
          <div className="application-section">
            <h3>Cause Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Cause Name</label>
                <div>{project.name || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>Cause URL</label>
                <div>{project.url || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>Location</label>
                <div>{`${project.location?.city || ''}, ${project.location?.state || ''}`}</div>
              </div>
              <div className="info-item full-width">
                <label>Mission Statement</label>
                <div className="long-text">{project.missionStatement || 'N/A'}</div>
              </div>
              <div className="info-item full-width">
                <label>Vision & Goals</label>
                <div className="long-text">{project.visionGoals || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>Charitable Category</label>
                <div>{project.charitableCategory || 'N/A'}</div>
              </div>
              <div className="info-item full-width">
                <label>Funds Usage</label>
                <div className="tags-container">
                  {project.fundsUsage?.map((usage, index) => (
                    <span key={index} className="tag">{usage}</span>
                  )) || 'N/A'}
                </div>
              </div>
            </div>
          </div>

          {/* Fundraising Plans - Enhanced */}
          <div className="application-section">
            <h3>Fundraising Plans</h3>
            <div className="info-grid">
              <div className="info-item full-width">
                <label>Fundraising Methods</label>
                <div className="tags-container">
                  {fundraising.plans?.map((plan, index) => (
                    <span key={index} className="tag">{plan}</span>
                  )) || 'N/A'}
                </div>
              </div>
              <div className="info-item">
                <label>Grant Interest</label>
                <div>{fundraising.grantInterest || 'N/A'}</div>
              </div>
              <div className="info-item full-width">
                <label>Grant Details</label>
                {fundraising.grantInterest === 'Yes' ? (
                  <table className="details-table">
                    <thead>
                      <tr>
                        <th>Funder</th>
                        <th>Amount</th>
                        <th>Restrictions</th>
                        <th>RFP Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fundraising.grantDetails?.map((grant, index) => (
                        <tr key={index}>
                          <td>{grant.funder}</td>
                          <td>{formatCurrency(grant.amount)}</td>
                          <td>{grant.restrictions}</td>
                          <td>{grant.rfp}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="not-applicable">Not applicable - No grant interest indicated</div>
                )}
              </div>
            </div>
          </div>

          {/* Financial Information */}
          <div className="application-section">
            <h3>Financial Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>First Year Revenue</label>
                <div>{formatCurrency(financial.revenueProjections?.yearOne || 0)}</div>
              </div>
              <div className="info-item">
                <label>Second Year Revenue</label>
                <div>{formatCurrency(financial.revenueProjections?.yearTwo || 0)}</div>
              </div>
              <div className="info-item full-width">
                <label>Budget Details</label>
                <table className="details-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* First show actual budget items */}
                    {financial.budgetDetails?.map((item, index) => (
                      <tr key={index}>
                        <td>{item.category}</td>
                        <td>{item.description || 'No details provided'}</td>
                        <td>{formatCurrency(item.amount)}</td>
                      </tr>
                    ))}
                    
                    {/* Then show any missing categories */}
                    {[
                      'Staff/Personnel',
                      'Professional Services',
                      'Equipment',
                      'Travel',
                      'Marketing',
                      'Events',
                      'Office Space',
                      'Supplies',
                      'Technology',
                      'Insurance',
                      'Other'
                    ].filter(category => 
                      !financial.budgetDetails?.some(item => 
                        item.category === category
                      )
                    ).map(category => (
                      <tr key={category} className="empty-category">
                        <td>{category}</td>
                        <td>No details provided</td>
                        <td>$0</td>
                      </tr>
                    ))}
                    
                    <tr className="total-row">
                      <td colSpan="2"><strong>Total Budget</strong></td>
                      <td>
                        <strong>
                          {formatCurrency(
                            (financial.budgetDetails || [])
                              .reduce((sum, item) => sum + (Number(item.amount) || 0), 0)
                          )}
                        </strong>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="info-item">
                <label>Budget File</label>
                <div>
                  {financial.budgetFile ? (
                    <a href={financial.budgetFile} target="_blank" rel="noopener noreferrer">
                      View Budget Document
                    </a>
                  ) : 'No budget file uploaded'}
                </div>
              </div>
            </div>
          </div>

          {/* Organization Details - Enhanced */}
          <div className="application-section">
            <h3>Organization Details</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Applicant Type</label>
                <div>{organization.type || 'N/A'}</div>
              </div>
              
              {/* Individual Details */}
              <div className="info-item">
                <label>Individual Address</label>
                <div>
                  {organization.type === 'Individual' ? 
                    organization.individualAddress || 'N/A' : 
                    'Not applicable - Entity application'}
                </div>
              </div>

              {/* Entity Details */}
              <div className="info-item">
                <label>Entity EIN</label>
                <div>
                  {organization.type === 'Entity' ? 
                    organization.entityEIN || 'N/A' : 
                    'Not applicable - Individual application'}
                </div>
              </div>
              <div className="info-item">
                <label>Entity Documents</label>
                <div>
                  {organization.type === 'Entity' ? (
                    organization.entityDocs ? (
                      <a href={organization.entityDocs} target="_blank" rel="noopener noreferrer">
                        View Entity Documents
                      </a>
                    ) : 'No documents uploaded'
                  ) : 'Not applicable - Individual application'}
                </div>
              </div>
            </div>
          </div>

          {/* Referral Information */}
          <div className="application-section">
            <h3>Referral Information</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>How did you hear about us?</label>
                <div>{referral.heardAboutUs || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>Referral Source</label>
                <div>{referral.referralSource || 'N/A'}</div>
              </div>
              <div className="info-item">
                <label>Other Source Details</label>
                <div>
                  {referral.heardAboutUs === 'Other' ? 
                    referral.otherSource || 'N/A' : 
                    'Not applicable - Standard referral source selected'}
                </div>
              </div>
            </div>
          </div>

          {/* Previous Fiscal Sponsorship */}
          <div className="application-section">
            <h3>Previous Fiscal Sponsorship</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Previously Sponsored</label>
                <div>{previousSponsorship.hadPreviousSponsor ? 'Yes' : 'No'}</div>
              </div>
              {previousSponsorship.hadPreviousSponsor && (
                <>
                  <div className="info-item">
                    <label>Funds to Transfer</label>
                    <div>{previousSponsorship.fundsToTransfer ? 'Yes' : 'No'}</div>
                  </div>
                  {previousSponsorship.fundsToTransfer && (
                    <div className="info-item">
                      <label>Transfer Amount</label>
                      <div>{formatCurrency(previousSponsorship.transferAmount)}</div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Social Media */}
          <div className="application-section">
            <h3>Social Media Presence</h3>
            <div className="info-grid">
              <div className="info-item">
                <label className="social-label">
                  <FontAwesomeIcon icon={faLinkedin} /> LinkedIn
                </label>
                <div>
                  {socialMedia.linkedin ? (
                    <a 
                      href={socialMedia.linkedin} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      {socialMedia.linkedin}
                    </a>
                  ) : 'N/A'}
                </div>
              </div>
              <div className="info-item">
                <label className="social-label">
                  <FontAwesomeIcon icon={faInstagram} /> Instagram
                </label>
                <div>
                  {socialMedia.instagram ? (
                    <a 
                      href={socialMedia.instagram} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      {socialMedia.instagram}
                    </a>
                  ) : 'N/A'}
                </div>
              </div>
              <div className="info-item">
                <label className="social-label">
                  <FontAwesomeIcon icon={faFacebookSquare} /> Facebook
                </label>
                <div>
                  {socialMedia.facebook ? (
                    <a 
                      href={socialMedia.facebook} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-link"
                    >
                      {socialMedia.facebook}
                    </a>
                  ) : 'N/A'}
                </div>
              </div>
              <div className="info-item">
                <label>Other Social Media</label>
                <div>{socialMedia.otherSocials || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Additional Comments */}
          <div className="application-section">
            <h3>Additional Comments</h3>
            <div className="info-grid">
              <div className="info-item full-width">
                <div className="long-text">{additionalInfo.comments || 'No additional comments provided.'}</div>
              </div>
            </div>
          </div>

          {/* Application History */}
          <div className="application-section">
            <h3>Application History</h3>
            <div className="timeline">
              {metadata.history?.map((event, index) => (
                <div key={index} className="timeline-item">
                  <div className="timeline-date">{formatDate(event.timestamp)}</div>
                  <div className="timeline-content">
                    <strong>{event.action}</strong>
                    <p>{event.notes}</p>
                  </div>
                </div>
              )) || 'No history available'}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="modal-footer">
          <div className="status-section">
            <span className={`status-badge ${status}`}>
              {status}
            </span>
            <span className="submission-date">
              Submitted: {formatDate(metadata.submittedAt)}
            </span>
          </div>
        </div>

        {showDenyModal && (
          <DenyModal
            application={application}
            onClose={() => setShowDenyModal(false)}
            onConfirm={handleDenyConfirm}
          />
        )}
      </div>
    </div>
  );
};

export default ApplicationModal; 